import type {
  BuddySearchRequest,
  BuddySearchResultUser,
} from '@wlb/shared/types/userProfile';
import { lbsToKgPrecise } from '@wlb/shared/utils/weightUtils';

export type BuddyGender = 'All' | 'Male' | 'Female' | 'Other';
export type BuddyWeightUnit = 'kg' | 'lbs';
export type BuddyLoseMode = 'any' | 'range';

export const BUDDY_AGE_MIN = 18;
export const BUDDY_AGE_MAX = 75;
export const DEFAULT_BUDDY_AGE: [number, number] = [
  BUDDY_AGE_MIN,
  BUDDY_AGE_MAX,
];
export const BUDDY_WEIGHT_MIN_KG = 36;
export const BUDDY_WEIGHT_MAX_KG = 127;
export const DEFAULT_BUDDY_WEIGHT_KG: [number, number] = [
  BUDDY_WEIGHT_MIN_KG,
  BUDDY_WEIGHT_MAX_KG,
];
export const BUDDY_LOSS_MIN_KG = 2;
export const BUDDY_LOSS_MAX_KG = 27;
export const DEFAULT_BUDDY_LOSS_RANGE_KG: [number, number] = [
  BUDDY_LOSS_MIN_KG,
  BUDDY_LOSS_MAX_KG,
];

const LEGACY_DEFAULT_BUDDY_AGE: [number, number] = [25, 45];
const LEGACY_DEFAULT_BUDDY_WEIGHT_KG: [number, number] = [78, 111];
const LEGACY_LOSS_RANGE_PADDING_KG = 5;
const BUDDY_SEARCH_STORAGE_VERSION = 4;

export interface BuddySearchFilters {
  search: string;
  gender: BuddyGender;
  ageMin: number;
  ageMax: number;
  countryIds: number[];
  weightKg: [number, number];
  weightUnit: BuddyWeightUnit;
  loseMode: BuddyLoseMode;
  loseKg: [number, number];
  loseUnit: BuddyWeightUnit;
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const finiteNumber = (value: unknown): number | undefined =>
  typeof value === 'number' && Number.isFinite(value) ? value : undefined;

const nonEmptyString = (value: unknown): string | undefined =>
  typeof value === 'string' && value.trim() ? value.trim() : undefined;

const optionalString = (value: unknown): string | undefined =>
  nonEmptyString(value);

const validCountryIds = (value: unknown): number[] => {
  if (!Array.isArray(value)) return [];
  return Array.from(new Set(value.filter(
    (countryId): countryId is number =>
      typeof countryId === 'number' &&
      Number.isInteger(countryId) &&
      countryId > 0,
  )));
};

const validRange = (value: unknown): [number, number] | undefined => {
  if (!Array.isArray(value) || value.length !== 2) return undefined;
  const low = finiteNumber(value[0]);
  const high = finiteNumber(value[1]);
  return low !== undefined && high !== undefined ? [low, high] : undefined;
};

export const normalizeBuddySearchResults = (value: unknown): BuddySearchResultUser[] => {
  if (!Array.isArray(value)) return [];

  return value.flatMap((row): BuddySearchResultUser[] => {
    if (!isRecord(row)) return [];

    const userId = finiteNumber(row.userId);
    const userGuid = nonEmptyString(row.userGuid) ?? nonEmptyString(row.profileUserGuid);
    const userName = nonEmptyString(row.userName);
    if (userId === undefined || !userGuid || !userName) return [];

    const normalized: BuddySearchResultUser = {
      userId,
      userGuid,
      profileUserGuid: optionalString(row.profileUserGuid),
      userName,
      userPhoto: optionalString(row.userPhoto) ?? '',
      weightToLose: finiteNumber(row.weightToLose) ?? 0,
      weightToLoseUnit: optionalString(row.weightToLoseUnit) ?? '',
      matchPercentage: finiteNumber(row.matchPercentage) ?? 0,
      isRequested: row.isRequested === true,
      status: optionalString(row.status),
      isBuddy: row.isBuddy === true,
      invited: row.invited === true,
      isAccepted: row.isAccepted === true,
      completedAge: finiteNumber(row.completedAge),
      location: optionalString(row.location),
      lostWeight: optionalString(row.lostWeight),
      description: optionalString(row.description),
    };

    return [normalized];
  });
};

export const getBuddySearchResults = (response: unknown): BuddySearchResultUser[] => {
  if (!isRecord(response) || !isRecord(response.data)) return [];
  return normalizeBuddySearchResults(response.data.buddies);
};

/** The profile endpoint expects its dedicated profile GUID when one is available. */
export const getBuddyProfileGuid = (user: BuddySearchResultUser): string =>
  nonEmptyString(user.profileUserGuid) ?? user.userGuid;

export const filterBuddySearchResultsByTargetLoss = (
  results: BuddySearchResultUser[],
  loseMode: BuddyLoseMode,
  loseKg: [number, number],
): BuddySearchResultUser[] => {
  if (loseMode === 'any') return results;

  return results.filter(result => {
    const unit = result.weightToLoseUnit.trim().toLowerCase();
    const weightKg = unit.startsWith('lb')
      ? lbsToKgPrecise(result.weightToLose)
      : unit.startsWith('kg')
        ? result.weightToLose
        : Number.NaN;
    return Number.isFinite(weightKg) &&
      weightKg >= loseKg[0] &&
      weightKg <= loseKg[1];
  });
};

export const applyBuddyTargetLossFilter = (
  results: BuddySearchResultUser[],
  userName: string | undefined,
  loseMode: BuddyLoseMode,
  loseKg: [number, number],
): BuddySearchResultUser[] =>
  userName?.trim()
    ? results
    : filterBuddySearchResultsByTargetLoss(results, loseMode, loseKg);

export const buildBuddySearchPayload = ({
  search,
  gender,
  ageMin,
  ageMax,
  countryIds,
  weightKg,
  weightUnit,
  loseUnit,
}: BuddySearchFilters): BuddySearchRequest => {
  const payload: BuddySearchRequest = {
    // BuddySearch currently exposes one exact target only. Range filtering is
    // applied to the returned rows so we do not send unsupported min/max keys.
    wantToLose: null,
    weightToLoseScaleUnit: loseUnit === 'lbs' ? 2 : 1,
  };

  const userName = search.trim();
  if (userName) {
    payload.userName = userName;
    return payload;
  }

  const filtersAge = ageMin !== BUDDY_AGE_MIN || ageMax !== BUDDY_AGE_MAX;
  if (filtersAge) {
    payload.ageFrom = ageMin;
    payload.ageTo = ageMax;
  }

  const filtersCurrentWeight =
    weightKg[0] !== BUDDY_WEIGHT_MIN_KG ||
    weightKg[1] !== BUDDY_WEIGHT_MAX_KG;
  if (filtersCurrentWeight) {
    payload.currentWeightFrom = weightKg[0];
    payload.currentWeightTo = weightKg[1];
    payload.currentWeightScaleUnit = weightUnit === 'lbs' ? 2 : 1;
  }

  if (gender !== 'All') {
    payload.gender = gender === 'Male' ? 1 : gender === 'Female' ? 2 : 3;
  }
  const countries = validCountryIds(countryIds);
  if (countries.length > 0) payload.country = countries;
  return payload;
};

export const readBuddySearchFilters = (
  rawValue: string | null,
): Partial<BuddySearchFilters> | null => {
  if (!rawValue) return null;

  try {
    const value: unknown = JSON.parse(rawValue);
    if (!isRecord(value)) return null;

    const filters: Partial<BuddySearchFilters> = {};
    if (typeof value.search === 'string') filters.search = value.search;
    if (value.gender === 'All' || value.gender === 'Male' ||
        value.gender === 'Female' || value.gender === 'Other') {
      filters.gender = value.gender;
    }
    const ageMin = finiteNumber(value.ageMin);
    const ageMax = finiteNumber(value.ageMax);
    if (ageMin !== undefined && ageMax !== undefined) {
      const isLegacyDefault =
        value.version !== BUDDY_SEARCH_STORAGE_VERSION &&
        ageMin === LEGACY_DEFAULT_BUDDY_AGE[0] &&
        ageMax === LEGACY_DEFAULT_BUDDY_AGE[1];
      [filters.ageMin, filters.ageMax] = isLegacyDefault
        ? DEFAULT_BUDDY_AGE
        : [ageMin, ageMax];
    } else {
      if (ageMin !== undefined) filters.ageMin = ageMin;
      if (ageMax !== undefined) filters.ageMax = ageMax;
    }
    filters.countryIds = validCountryIds(
      value.countryIds ?? (finiteNumber(value.countryId) ? [value.countryId] : []),
    );
    const weightKg = validRange(value.weightKg);
    if (weightKg) {
      const isLegacyDefault =
        value.version !== BUDDY_SEARCH_STORAGE_VERSION &&
        weightKg[0] === LEGACY_DEFAULT_BUDDY_WEIGHT_KG[0] &&
        weightKg[1] === LEGACY_DEFAULT_BUDDY_WEIGHT_KG[1];
      filters.weightKg = isLegacyDefault
        ? DEFAULT_BUDDY_WEIGHT_KG
        : weightKg;
    }
    if (value.weightUnit === 'kg' || value.weightUnit === 'lbs') {
      filters.weightUnit = value.weightUnit;
    }
    if (value.loseMode === 'any' || value.loseMode === 'range') {
      filters.loseMode = value.loseMode;
    }
    const loseKg = validRange(value.loseKg);
    if (loseKg) {
      filters.loseKg = loseKg;
    } else {
      const legacyLoseKg = finiteNumber(value.loseKg);
      if (legacyLoseKg !== undefined) {
        filters.loseKg = [
          Math.max(BUDDY_LOSS_MIN_KG, legacyLoseKg - LEGACY_LOSS_RANGE_PADDING_KG),
          Math.min(BUDDY_LOSS_MAX_KG, legacyLoseKg + LEGACY_LOSS_RANGE_PADDING_KG),
        ];
      }
    }
    if (value.loseUnit === 'kg' || value.loseUnit === 'lbs') {
      filters.loseUnit = value.loseUnit;
    }
    return filters;
  } catch {
    return null;
  }
};

export const loadBuddySearchFilters = (
  storage: Pick<Storage, 'getItem'> | null,
  key: string,
): Partial<BuddySearchFilters> | null => {
  if (!storage) return null;
  try {
    return readBuddySearchFilters(storage.getItem(key));
  } catch {
    return null;
  }
};

export const writeBuddySearchFilters = (
  storage: Pick<Storage, 'setItem'> | null,
  key: string,
  filters: BuddySearchFilters,
): boolean => {
  if (!storage) return false;
  try {
    storage.setItem(key, JSON.stringify({
      version: BUDDY_SEARCH_STORAGE_VERSION,
      ...filters,
    }));
    return true;
  } catch {
    return false;
  }
};

export const getBrowserSessionStorage = (): Storage | null => {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
};

export const isSuccessfulBuddyAction = (response: unknown): boolean =>
  !isRecord(response) ||
  (response.success !== false &&
    (finiteNumber(response.statusCode) === undefined ||
      (Number(response.statusCode) >= 200 && Number(response.statusCode) < 300)));

export const getBuddyActionMessage = (response: unknown): string | undefined =>
  isRecord(response) ? nonEmptyString(response.message) : undefined;
